import { useState } from 'react';
import axios from 'axios';
import { useDispatch, useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { getBlockNameByType } from 'lib/blocks';
import { getFormattedSize } from 'lib/common';
import { Alert, Button } from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { Icon } from 'components/common/icons';

import type { MappedFiles } from 'components/courses/manage/ManageExportScorm';

interface Props {
  excludedFiles: MappedFiles;
}

const CourseDownloadScormExcludedFiles = ({ excludedFiles }: Props) => {
  const { dispatch } = useDispatch();

  const { messages } = useReduxState();

  const blockNameByType = getBlockNameByType({ messages });

  const fileIds = Object.keys(excludedFiles);

  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});

  const handleDonwload = async (fileId: string) => {
    try {
      setLoading((prev) => ({ ...prev, [fileId]: true }));

      const file = excludedFiles[fileId];

      await saveBlob(file.url);
      if (file.thumbnail) await saveBlob(file.thumbnail);
      if (file.subtitles?.length) {
        for (const subtitle of file.subtitles) {
          await saveBlob(subtitle);
        }
      }
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setLoading((prev) => ({ ...prev, [fileId]: false }));
    }
  };

  const saveBlob = async (url: string) => {
    const filename = url.slice(url.lastIndexOf('/') + 1);
    const response = await axios.get(url, { responseType: 'blob' });
    const blob = new Blob([response.data]);

    // @ts-ignore
    if (window.navigator.msSaveBlob) {
      // download file in ie
      // @ts-ignore
      window.navigator.msSaveBlob(blob, filename);
    } else {
      // download file by simulating a link click
      const fileUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return fileIds.length > 0 ? (
    <Alert
      expand={true}
      text={
        <Box padding={0}>
          <BoxTable inline={true}>
            <BoxTableHeader>
              <BoxTableCell>{messages.commonType}</BoxTableCell>
              <BoxTableCell>{messages.commonDownload}</BoxTableCell>
            </BoxTableHeader>

            {fileIds.map((fileId) => (
              <BoxTableRow key={excludedFiles[fileId].url}>
                <BoxTableCell>
                  {`${
                    blockNameByType[excludedFiles[fileId].type]
                  } (${getFormattedSize(excludedFiles[fileId].size)})`}
                </BoxTableCell>

                <BoxTableCell>
                  <Button
                    category="light"
                    loading={loading[fileId]}
                    style={{ width: '100%' }}
                    text={
                      <span>
                        <Icon
                          icon="download"
                          fixedWidth={true}
                          style={{ marginRight: 5 }}
                        />
                        {excludedFiles[fileId].url.slice(
                          excludedFiles[fileId].url.lastIndexOf('/') + 1
                        )}
                      </span>
                    }
                    onClick={() => handleDonwload(fileId)}
                  />
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        </Box>
      }
      title={getMessageWithPlaceholders({
        message: messages.manageExportScormExcludedFiles,
        placeholders: { numberOfFiles: fileIds.length }
      })}
      style={{ margin: '-5px 0 15px 0' }}
      type="warning-light"
    />
  ) : null;
};

export default CourseDownloadScormExcludedFiles;
